import UserTypes from '../user/user.types';

import { Middleware } from 'redux';
import AnyAction from 'store/anyActions';
import RootState from 'store/state';
import { authenticateUser, authError, mfaRequired } from 'store/user/user.actions';
import AppTypes from 'store/app/app.types';

import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js';
import AppState, { CognitoType } from 'store/app/app.state';

const urlparams = new URLSearchParams(window.location.hash.replace('#', ''));

let attributes: any;
let user: CognitoUser | null;

const getCurrentUserToken = (cognito: CognitoType): Promise<string | undefined> => {
    //  eslint-disable-next-line
    return new Promise(async resolve => {
        if (cognito.type !== 'in_app') return resolve();
        const pool = new CognitoUserPool({
            ClientId: cognito.client_id,
            UserPoolId: cognito.userPoolId,
        });
        user = pool.getCurrentUser();
        if (!user) return resolve();
        try{
            const session = await getUserSession(user)
            if(!session.isValid()) return resolve()
            const token = session.getAccessToken().getJwtToken();
            const idToken = session.getIdToken().getJwtToken();
            localStorage.setItem('oauthToken', idToken);
            resolve(token);
        }
        catch(ex){
            resolve();
        }
    });
};

const getUserSession = (user:CognitoUser):Promise<CognitoUserSession> => {
    return new Promise((resolve,reject)=>{
        user.getSession((err: any, session: any) => {
            if(err) return reject()
            return resolve(session)
        })
    })
}

const getCognitoUser = (username: string, cognito: CognitoType) => {
    if (cognito.type === 'in_app') {
        const pool = new CognitoUserPool({
            ClientId: cognito.client_id,
            UserPoolId: cognito.userPoolId,
        });

        return new CognitoUser({
            Username: username,
            Pool: pool,
        });
    }
};

function isCognito(identityManagement?:string) {
    return identityManagement === 'cognito';
}

// eslint-disable-next-line
const refreshToken = async () => {
    if(!user) return;
    const session = await getUserSession(user);
    //Only refresh if due to expire in the next 30mins
    if(session.getIdToken().getExpiration() < Math.floor((Date.now() + 1000*60*30) / 1000)) return;

    user.refreshSession(session.getRefreshToken(), (err, session: CognitoUserSession) => {
        if(err || !session.isValid()) return;
        const idToken = session.getIdToken().getJwtToken();
        localStorage.setItem('oauthToken', idToken);
    });
}

const cognitoMiddleware: Middleware<{}, RootState> = store => next => async (action: AnyAction) => {
    const { cognito, identityManagement } = store.getState().app;

    switch (action.type) {
        case AppTypes.INIT_APP_SUCCESS: {
            const { identityManagement, cognito } = action.payload as AppState;
            if (!isCognito(identityManagement) || !cognito) return next(action);

            next(action);
            if (cognito.type === 'redirect') {
                const token = urlparams.get('access_token');
                if (token) {
                    store.dispatch(authenticateUser(token, true) as any);
                    window.location.hash = '';
                }
            } else if (cognito.type === 'in_app') {
                getCurrentUserToken(cognito).then(token => {
                    if (token) {
                        store.dispatch(authenticateUser(token, true) as any);
                    }
                });
            }
            return;
        }
        case UserTypes.LOGIN:{
            const token = urlparams.get('access_token');
            if (isCognito(identityManagement) && cognito && !token) {
                console.log('logging in as cognito');
                
                if (
                    cognito.type === 'in_app' &&
                    action.payload.password &&
                    action.payload.username
                ) {
                    store.dispatch({
                        type: UserTypes.AUTHENTICATING_USER,
                    });

                    const auth = new AuthenticationDetails({
                        Username: action.payload.username,
                        Password: action.payload.password,
                    });

                    user = getCognitoUser(action.payload.username, cognito)!;

                    user &&
                        user.authenticateUser(auth, {
                            mfaRequired: (challangeName:any,challengeParameters:any) => {
                                console.log(challangeName,challengeParameters);
                                store.dispatch(mfaRequired({
                                    type:challengeParameters.CODE_DELIVERY_DELIVERY_MEDIUM,
                                    destination: challengeParameters.CODE_DELIVERY_DESTINATION
                                }));
                            },
                            onSuccess: result => {
                                const token = result.getAccessToken();
                                localStorage.setItem('oauthToken', result.getIdToken().getJwtToken());
                                store.dispatch(authenticateUser(token.getJwtToken(), true) as any);
                            },
                            onFailure: err => {
                                store.dispatch(
                                    authError(
                                        // dont pass error message for security
                                        'Incorrect username or password'
                                    )
                                );
                            },
                            newPasswordRequired: (userAttributes, requiredAttributes) => {
                                // Required attributes generally set as an empty array, but if there exist any required attributes then set their values in attributes obj
                                Array.isArray(requiredAttributes) && requiredAttributes.forEach(a => {
                                    attributes[a] = userAttributes[a]
                                })

                                return store.dispatch({
                                    type: UserTypes.NEW_PASSWORD_REQUIRED,
                                });
                            },
                        });
                } else if (cognito.type === 'redirect') {
                    window.location.href = `${cognito.domain}/login?response_type=token&client_id=${cognito.client_id}&redirect_uri=${window.location.href}&scope=openid+profile+aws.cognito.signin.user.admin`;
                }
            }
            return next(action);
        }
        case UserTypes.FORGOT_PASSWORD_GET_CODE:
            if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                store.dispatch({
                    type: UserTypes.AUTHENTICATING_USER,
                });

                user = getCognitoUser(action.payload.username, cognito)!;

                user.forgotPassword({
                    onSuccess: () => {
                        next(action);
                    },
                    onFailure: err => {
                        if(err.name === 'UserNotFoundException'){
                            return store.dispatch(
                                authError( 'User not found' )
                            );
                        }
                        return store.dispatch(
                            authError(err.message)
                        );

                    },
                });
            }
            return;

        case UserTypes.FORGOT_PASSWORD_CONFIRM:
            if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                store.dispatch({
                    type: UserTypes.AUTHENTICATING_USER,
                });

                const { verificationCode, newPassword } = action.payload;

                user &&
                    user.confirmPassword(verificationCode, newPassword, {
                        onSuccess: () => {
                            return next(action);
                        },
                        onFailure: err => {
                            store.dispatch(authError(err.message));
                        },
                    });
            }
            return next(action);

        case UserTypes.NEW_PASSWORD_REQUIRED_CHANGE:
            if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                const { newPassword } = action.payload;

                store.dispatch({
                    type: UserTypes.AUTHENTICATING_USER,
                });

                user &&
                    user.completeNewPasswordChallenge(newPassword, attributes, {
                        onSuccess: result => {
                            const token = result.getAccessToken();
                            store.dispatch(authenticateUser(token.getJwtToken(), true) as any);
                        },
                        onFailure: err => {
                            store.dispatch(authError(err.message));
                        },
                    });
            }
            return next(action);
        case UserTypes.SUBMIT_MFA:
            if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {

                store.dispatch({
                    type: UserTypes.AUTHENTICATING_USER,
                });

                user &&
                    user.sendMFACode(action.payload, {
                        onSuccess: result => {
                            const token = result.getAccessToken();
                            store.dispatch(authenticateUser(token.getJwtToken(), true) as any);
                        },
                        onFailure: err => {
                            store.dispatch(authError(err.message));
                        },
                    });
            }
            return next(action);
        case AppTypes.CCP_LOGOUT:
        case UserTypes.LOGOUT:
            next(action);
            if (identityManagement === 'cognito' && cognito && cognito.type === 'redirect') {
                // if using the redirect cognito integration open a new tab with the logout url needed to logout of cognito and ADFS
                 window.open(`${cognito.domain}/logout?client_id=${cognito.client_id}&logout_uri=${cognito.logoutUrl}`, '_blank');
            }
   
            if (user) {
                user.signOut();
            }
            return;
        default:
            return next(action);
    }
};

export default cognitoMiddleware;
